/**
 * The legacy `SigningBlock` (packages/templates atoms/SigningBlock.tsx) as
 * a block — one implementation for every family.
 *
 *  - Statement: opt-in per instance (`statement: true`). Which wording
 *    prints is coupled to the place/date toggle, exactly as in the legacy
 *    atom: place/date ON ⇒ the physical "N likalydande exemplar" wording
 *    (N = signing parties, cardinal word); OFF ⇒ the digital wording when
 *    the host renders for Scrive (`digitalStatement`), else nothing.
 *  - Place/date: the host's `ctx.custom.signing = { includePlaceDate,
 *    digitalStatement }` (legacy SignIncludePlaceDateContext, default ON,
 *    and SignDigitalStatementContext, default OFF). An instance may pin
 *    either with `include_place_date` / `digital_statement`.
 *  - Panels: one per signer of each `(role, ordinal)` signing party, via the
 *    framework's `signerPanels` — so every panel carries the identity the
 *    renderer's measurement pass (`HtmlRenderOptions.signing.measure`)
 *    prints `@RSIG|…` markers for. Only the roster's own names print; no
 *    party text is formatted from the context.
 *  - Gating: a party row with `when_set` (or a `visible_when` condition)
 *    prints only when that holds — always on a blank form.
 *  - Frame: an optional section (`ref`, `title`, fixed `number` or
 *    `numbering: 'none'`), an optional lead paragraph, and the bold
 *    "Underskrifter" heading (`heading: false` hides it, a string renames it).
 */
import { evaluateCondition, group, nodes, p, section, signature, signerPanels, signingLabelFor, strong, t } from '@hyranai/regta-document-ir';
import { str } from '../lib.js';
import { sectionPlacement } from './fields.js';
import { isFilled } from './slots.js';
/** Copy supplied by legal — verbatim from the legacy `SIGNING_STATEMENT_DIGITAL`. */
export const SIGNING_STATEMENT_DIGITAL = 'Avtalet har upprättats och signerats digitalt, varpå parterna har tagit var sitt digitalt exemplar; datum för signering framgår av vattenstämpeln.';
const SWEDISH_CARDINALS = ['noll', 'ett', 'två', 'tre', 'fyra', 'fem', 'sex', 'sju', 'åtta', 'nio', 'tio'];
/** Swedish cardinal for small counts; digits past the table. */
export const swedishCardinal = (n) => SWEDISH_CARDINALS[n] ?? String(n);
/**
 * Legacy `signingStatementPhysical`: one likalydande original per signing
 * party, "två" for a degenerate count below two.
 */
export function signingStatementPhysical(partyCount) {
    const word = swedishCardinal(partyCount >= 2 ? partyCount : 2);
    return `Avtalet har upprättats i ${word} likalydande exemplar, varvid parterna har tagit var sitt original.`;
}
const bool = (value) => (typeof value === 'boolean' ? value : undefined);
/** Instance input first, then the host's `ctx.custom.signing`, then the legacy defaults. */
export function resolveSigning(input, ctx) {
    const host = (ctx.custom?.signing ?? {});
    return {
        includePlaceDate: bool(input.include_place_date) ?? bool(host.includePlaceDate) ?? true,
        digitalStatement: bool(input.digital_statement) ?? bool(host.digitalStatement) ?? false,
    };
}
/** The statement the legacy atom printed for `partyCount` signing parties, or `null`. */
export function signingStatementText(signing, partyCount) {
    if (signing.includePlaceDate)
        return signingStatementPhysical(partyCount);
    return signing.digitalStatement ? SIGNING_STATEMENT_DIGITAL : null;
}
function readParties(value) {
    if (!Array.isArray(value))
        return [];
    return value.flatMap((entry) => {
        if (typeof entry !== 'object' || entry === null)
            return [];
        const row = entry;
        const role = str(row.role);
        if (!role)
            return [];
        const visible = row.visible_when;
        return [
            {
                label: str(row.label) ?? role,
                role,
                ordinal: Number(row.ordinal ?? 0) || 0,
                // `when_key` / `when` are the pre-kit spellings, read for migration ease.
                whenSet: str(row.when_set) ?? str(row.when_key) ?? str(row.when),
                visibleWhen: visible && typeof visible === 'object' ? visible : null,
            },
        ];
    });
}
/** The signing parties that print for these values (every one on a blank form). */
export function includedSigningParties(input, ctx) {
    return readParties(input.parties).filter((ref) => ctx.mode === 'blank' ||
        ((ref.whenSet == null || isFilled(input[ref.whenSet])) && (ref.visibleWhen == null || evaluateCondition(ref.visibleWhen, input))));
}
/** Signature panels for the included parties; `showPlaceDate` follows the resolved toggle. */
export function signingPanels(refs, ctx, includePlaceDate) {
    return refs.flatMap((ref) => signerPanels({
        label: ref.label,
        role: ref.role,
        ordinal: ref.ordinal,
        party: ctx.parties.slot(ref.role, ref.ordinal),
        showPlaceDate: includePlaceDate,
    }));
}
const DEFAULT_HEADING = 'Underskrifter';
function headingOf(value) {
    if (value === false)
        return null;
    if (typeof value === 'string')
        return value.trim() === '' ? null : value;
    return DEFAULT_HEADING;
}
/**
 * The legacy SigningBlock's nodes: lead → statement → heading → panels, the
 * last three in one keep-together signature node. `[]` when no party prints.
 */
export function signingNodes(input, ctx) {
    const refs = includedSigningParties(input, ctx);
    if (refs.length === 0)
        return [];
    const signing = resolveSigning(input, ctx);
    const statement = input.statement === true ? signingStatementText(signing, refs.length) : null;
    const heading = headingOf(input.heading);
    const lead = str(input.lead);
    return nodes(lead ? p(input.lead_strong === true ? [strong(lead)] : lead, { keepWithNext: true }) : null, signature(signingPanels(refs, ctx, signing.includePlaceDate), {
        columns: 2,
        keepTogether: true,
        ...(statement ? { statement: [t(statement)] } : {}),
        ...(heading ? { heading } : {}),
    }));
}
export const SIGNING_PARTIES_SLOT = {
    key: 'parties',
    label: 'Undertecknande parter',
    type: 'list',
    required: true,
    repeats: true,
    columns: [
        { key: 'label', label: 'Etikett', type: 'text' },
        { key: 'role', label: 'Roll', type: 'party' },
        { key: 'ordinal', label: 'Ordning', type: 'number' },
        { key: 'when_set', label: 'Endast när fältet är ifyllt', type: 'text' },
    ],
};
/**
 * A signing block under a family's own block key, sharing this
 * implementation. Configuration (parties, statement, frame) is instance
 * input, not slots — so it never surfaces as a form field.
 */
export function signingBlockDefinition(spec) {
    return {
        blockKey: spec.blockKey,
        version: spec.version ?? 'v1',
        plane: 'shared',
        status: 'published',
        language: 'sv-SE',
        title: spec.title ?? 'Underskrifter',
        summary: spec.summary ??
            'Signature panels per signing party, with the legacy execution statement (physical vs digital, coupled to the place/date lines).',
        ...(spec.legalReview ? { legalReview: spec.legalReview } : {}),
        slots: [...(spec.defaults?.parties ? [] : [SIGNING_PARTIES_SLOT]), ...(spec.slots ?? [])],
        compile: (rawInput, ctx) => {
            const input = { ...(spec.defaults ?? {}), ...rawInput };
            const signing = signingNodes(input, ctx);
            if (signing.length === 0)
                return null;
            const content = [...(spec.prelude?.(input, ctx) ?? []), ...signing];
            const ref = str(input.ref);
            if (!ref)
                return content.length === 1 ? content : [group(content, { keepTogether: true })];
            const { title, opts } = sectionPlacement(input, { ref, title: DEFAULT_HEADING });
            return [section(ref, title, content, opts)];
        },
    };
}
/** `common.signing@v1` — the shared signing block. */
export const signingBlock = signingBlockDefinition({ blockKey: 'common.signing' });
/**
 * A composition's `parties` rows from its declared party slots: the slots
 * that sign, labelled with `signingLabel ?? label`, gated by their own
 * `visibleWhen`.
 */
export function signingPartiesFor(slots) {
    return slots
        .filter((slot) => slot.signs !== false)
        .map((slot) => ({
        label: signingLabelFor(slot),
        role: slot.role,
        ordinal: slot.ordinal ?? 0,
        ...(slot.visibleWhen ? { visible_when: slot.visibleWhen } : {}),
    }));
}
//# sourceMappingURL=signing.js.map